import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { Tooltip } from 'antd';
import { extent, max } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import React, { useMemo } from 'react';
import { CompanyActivity } from '../../models/company-activity-interface';
import * as allCurves from '@visx/curve';
import { GabiiErrorsPerDay, GabiiUsagePerDay } from '../../../models/gabii-statistics-interface';

// accessors
const getDate = (d: GabiiUsagePerDay) => new Date(d.date);
const getCount = (d: GabiiUsagePerDay) => d.userMessageCount;

const getErrorDate = (d: GabiiErrorsPerDay) => new Date(d.date);
const getErrorCount = (d: GabiiErrorsPerDay) => d.errorCount;

const formatAxisDate = timeFormat('%b %e');

export type UsageChartProps = {
  width: number;
  height: number;
  data: GabiiUsagePerDay[] | undefined;
  errorData: GabiiErrorsPerDay[] | undefined;
  isLoading: boolean;
};

export default function UsageChart({
  width,
  height,
  data = getInitialData(),
  errorData = getInitialData(),
  isLoading
}: UsageChartProps) {
  // bounds
  const innerWidth = width - 80;
  const innerHeight = height === 0 ? 0 : height - 50 - (width > 350 ? 30 : 0);

  // scales
  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [50, innerWidth + 50],
        domain: extent(data, getDate) as [Date, Date],
        nice: true,
      }),
    [data, innerWidth]
  );
  const countValueScale = useMemo(
    () =>
      scaleLinear({
        range: [innerHeight, 0],
        domain: [0, (max(data, getCount) || 0) + innerHeight / 3],
        nice: true,
      }),
    [innerHeight, data]
  );

  if (width < 10) return null;

  return (
    <svg
      width={width}
      height={height > 50 ? height - 50 : 0}
      style={{ backgroundColor: '#fff' }}
    >
      {data.map((d, j) => (
        <Tooltip
          key={j}
          title={
            <div>
              <b>
                {new Date(d.date).toLocaleDateString('en-us', {
                  month: 'short',
                  day: 'numeric',
                })}
              </b>
              <br />
              <div>{`Usage Count: ${d.userMessageCount}`}</div>
            </div>
          }
          color='#1D8585'
        >
          <circle
            className='informational-text'
            key={j}
            r={4}
            cx={dateScale(getDate(d))}
            cy={countValueScale(getCount(d))}
            stroke={isLoading ? '#767878' : '#1D8585'}
            fill={isLoading ? '#767878' : '#1D8585'}
          />
        </Tooltip>
      ))}
      <LinePath<CompanyActivity>
        curve={allCurves['curveNatural']}
        data={data}
        x={(d) => dateScale(getDate(d)) ?? 0}
        y={(d) => countValueScale(getCount(d)) ?? 0}
        stroke={isLoading ? '#767878' : '#1D8585'}
        strokeWidth={2}
        strokeOpacity={1}
        shapeRendering='geometricPrecision'
      />
      {errorData.map((d, j) => (
        <Tooltip
          key={j}
          title={
            <div>
              <b>
                {new Date(d.date).toLocaleDateString('en-us', {
                  month: 'short',
                  day: 'numeric',
                })}
              </b>
              <br />
              <div>{`Error Count: ${d.errorCount}`}</div>
            </div>
          }
          color='#76060C'
        >
          <circle
            className='informational-text'
            key={j}
            r={4}
            cx={dateScale(getErrorDate(d))}
            cy={countValueScale(getErrorCount(d))}
            stroke={isLoading ? '#767878' : '#76060C'}
            fill={isLoading ? '#767878' : '#76060C'}
          />
        </Tooltip>
      ))}
      <LinePath<CompanyActivity>
        curve={allCurves['curveNatural']}
        data={errorData}
        x={(d) => dateScale(getErrorDate(d)) ?? 0}
        y={(d) => countValueScale(getErrorCount(d)) ?? 0}
        stroke={isLoading ? '#767878' : '#76060C'}
        strokeWidth={2}
        strokeOpacity={1}
        shapeRendering='geometricPrecision'
      />
      <GridRows
        left={50}
        scale={countValueScale}
        width={innerWidth}
        strokeDasharray='1,3'
        stroke={'#000000'}
        strokeOpacity={0.5}
        pointerEvents='none'
        numTicks={5}
      />
      <AreaClosed<CompanyActivity>
        data={data}
        x={(d) => dateScale(getDate(d)) ?? 0}
        y={(d) => countValueScale(getCount(d)) ?? 0}
        yScale={countValueScale}
        strokeWidth={1}
        stroke='url(#area-gradient)'
        fill='url(#area-gradient)'
        curve={curveMonotoneX}
      />
      <AxisBottom
        top={innerHeight}
        scale={dateScale}
        tickFormat={formatAxisDate}
        tickLabelProps={() => ({
          fill: '#000000',
          fontSize: 12,
          textAnchor: 'middle',
        })}
        numTicks={7}
        hideAxisLine
        hideTicks
      />
      <AxisLeft
        left={20}
        scale={countValueScale}
        tickLabelProps={() => ({
          fill: '#000000',
          fontSize: 12,
        })}
        hideAxisLine
        hideTicks
        numTicks={5}
      />
    </svg>
  );
}

const getInitialData = () : CompanyActivity[] => [
 {
    date: getNumberOfDaysAgo(0),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(1),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(2),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(3),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(4),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(5),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 },
 {
    date: getNumberOfDaysAgo(6),
    activityCount: 0,
    userCount: 0,
    documentViews: 0
 }
]

const getNumberOfDaysAgo = (daysAgo: number) => {
    const now = new Date();
    const dateDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
    return dateDaysAgo;
}
